import React, { useState, useEffect, useRef, useCallback } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { useLocationStore } from "@/lib/store";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { PhaseCelebration } from "@/components/phase-celebration";
import {
  RTD_PHASES,
  getNewlyCompletedPhase,
  markPhaseCelebrated,
  type RtdPhase,
} from "@/lib/rtd-phases";

const LevelFifteenPage = React.lazy(() => import("@/pages/level-fifteen"));

const LEVELS = [
  {
    id: 1,
    name: "Foundation",
    subtitle: "Nail Your Core Identity",
    tasks: [
      { id: "geogrid", label: "Run a GeoGrid Scan", desc: "Map your local visibility so we know where you stand", route: "/geogrid" },
      { id: "category", label: "Category Optimization", desc: "Set primary & additional categories based on GeoGrid data", route: "/gbp-optimization" },
      { id: "description", label: "Business Description", desc: "AI-optimized GBP description with target keywords", route: "/gbp-optimization" },
      { id: "services", label: "Add Services", desc: "List all services your business offers", route: "/gbp-optimization" },
      { id: "service_desc", label: "Add Service Descriptions", desc: "Keyword-rich descriptions for each service", route: "/gbp-optimization" },
    ],
  },
  {
    id: 2,
    name: "Profile Strength",
    subtitle: "Build Trust & Completeness",
    tasks: [
      { id: "essentials", label: "Complete Profile Essentials", desc: "Hours, attributes, phone, website, address verified", route: "/profile-health" },
      { id: "reviews", label: "Reply to All Reviews", desc: "Respond to every review — good and bad", route: "/reviews" },
      { id: "images", label: "Add Images", desc: "Upload photos so AI can create personalized visuals", route: "/media" },
    ],
  },
  {
    id: 3,
    name: "Content Engine",
    subtitle: "Feed the Machine",
    tasks: [
      { id: "posts", label: "Posts Engine", desc: "Automated GBP post creation & scheduling", route: "/posts" },
      { id: "publish_post", label: "Publish a Post Now", desc: "Create and publish your first GBP post live", route: "/posts" },
      { id: "schedule_post", label: "Schedule a Post", desc: "Queue a post for future publishing", route: "/posts" },
      { id: "products", label: "Product Forge", desc: "Generate & optimize GBP product listings", route: "/products" },
      { id: "publish_product", label: "Publish a Product to GBP", desc: "Post an optimized product listing to your GBP", route: "/products" },
    ],
  },
  {
    id: 4,
    name: "Expand",
    subtitle: "Strengthen Your Web",
    tasks: [
      { id: "social_sync", label: "Sync Social Fortress", desc: "Connect all social profiles for brand signals", route: "/social" },
      { id: "missing_profiles", label: "Add Missing Socials", desc: "Claim unclaimed social & platform profiles", route: "/social" },
    ],
  },
];

const PILLAR_COLORS: Record<string, string> = {
  relevance: "#e8732a",
  prominence: "#4ecdc4",
  proximity: "#a78bfa",
  core: "#ff8c00",
};

interface NetworkNode {
  id: string;
  label: string;
  x: number;
  y: number;
  size: number;
  group: string;
}

const NETWORK_NODES: NetworkNode[] = [
  { id: "visibility", label: "Visibility", x: 0.5, y: 0.5, size: 62, group: "core" },
  { id: "relevance", label: "Relevance", x: 0.22, y: 0.27, size: 48, group: "relevance" },
  { id: "n_category", label: "Category", x: 0.07, y: 0.12, size: 22, group: "relevance" },
  { id: "n_description", label: "Description", x: 0.07, y: 0.28, size: 22, group: "relevance" },
  { id: "n_services", label: "Services", x: 0.07, y: 0.44, size: 22, group: "relevance" },
  { id: "n_service_desc", label: "Service\nDescriptions", x: 0.20, y: 0.48, size: 18, group: "relevance" },
  { id: "n_products", label: "Products", x: 0.24, y: 0.10, size: 18, group: "relevance" },
  { id: "n_posts_r", label: "Posts", x: 0.36, y: 0.16, size: 16, group: "relevance" },
  { id: "n_keywords", label: "Keywords", x: 0.36, y: 0.36, size: 16, group: "relevance" },
  { id: "prominence", label: "Prominence", x: 0.78, y: 0.27, size: 48, group: "prominence" },
  { id: "n_reviews", label: "Reviews", x: 0.93, y: 0.12, size: 24, group: "prominence" },
  { id: "n_images", label: "Photos &\nAI Images", x: 0.93, y: 0.30, size: 22, group: "prominence" },
  { id: "n_social", label: "Social\nFortress", x: 0.76, y: 0.10, size: 22, group: "prominence" },
  { id: "n_missing", label: "Missing\nSocials", x: 0.64, y: 0.12, size: 16, group: "prominence" },
  { id: "n_website", label: "Website", x: 0.80, y: 0.46, size: 18, group: "prominence" },
  { id: "n_schema", label: "Schema", x: 0.66, y: 0.38, size: 14, group: "prominence" },
  { id: "proximity", label: "Proximity", x: 0.5, y: 0.78, size: 48, group: "proximity" },
  { id: "n_address", label: "Address /\nService Area", x: 0.35, y: 0.90, size: 22, group: "proximity" },
  { id: "n_geogrid", label: "GeoGrid", x: 0.65, y: 0.90, size: 24, group: "proximity" },
  { id: "n_hours", label: "Hours", x: 0.50, y: 0.96, size: 18, group: "proximity" },
  { id: "n_local", label: "Local\nSignals", x: 0.37, y: 0.72, size: 16, group: "proximity" },
  { id: "n_nap", label: "NAP\nConsistency", x: 0.63, y: 0.72, size: 18, group: "proximity" },
];

const NETWORK_EDGES: [string, string][] = [
  ["relevance", "visibility"],
  ["prominence", "visibility"],
  ["proximity", "visibility"],
  ["n_category", "relevance"],
  ["n_description", "relevance"],
  ["n_services", "relevance"],
  ["n_service_desc", "relevance"],
  ["n_products", "relevance"],
  ["n_posts_r", "relevance"],
  ["n_keywords", "relevance"],
  ["n_reviews", "prominence"],
  ["n_images", "prominence"],
  ["n_social", "prominence"],
  ["n_missing", "prominence"],
  ["n_website", "prominence"],
  ["n_schema", "prominence"],
  ["n_address", "proximity"],
  ["n_geogrid", "proximity"],
  ["n_hours", "proximity"],
  ["n_local", "proximity"],
  ["n_nap", "proximity"],
  ["n_posts_r", "prominence"],
  ["n_reviews", "relevance"],
  ["n_nap", "prominence"],
  ["n_social", "relevance"],
  ["n_missing", "proximity"],
  ["n_images", "relevance"],
];

const TASK_TO_NODES: Record<string, string[]> = {
  geogrid: ["n_geogrid"],
  category: ["n_category", "n_keywords"],
  description: ["n_description", "n_keywords"],
  services: ["n_services"],
  service_desc: ["n_service_desc", "n_services"],
  essentials: ["n_hours", "n_address", "n_nap", "n_website"],
  reviews: ["n_reviews"],
  images: ["n_images"],
  posts: ["n_posts_r"],
  publish_post: ["n_posts_r"],
  schedule_post: ["n_posts_r"],
  products: ["n_products"],
  publish_product: ["n_products"],
  social_sync: ["n_social", "n_local"],
  missing_profiles: ["n_missing", "n_nap"],
};

const HUB_RULES: Record<string, string[]> = {
  relevance: ["n_category", "n_description", "n_services", "n_service_desc", "n_products", "n_posts_r", "n_keywords"],
  prominence: ["n_reviews", "n_images", "n_social", "n_missing", "n_website", "n_schema"],
  proximity: ["n_address", "n_geogrid", "n_hours", "n_local", "n_nap"],
};

const TASK_PILLARS: Record<string, string[]> = {};
Object.keys(TASK_TO_NODES).forEach((taskId) => {
  const pillars = new Set<string>();
  TASK_TO_NODES[taskId].forEach((nId) => {
    Object.entries(HUB_RULES).forEach(([hub, children]) => {
      if (children.includes(nId)) pillars.add(hub);
    });
  });
  TASK_PILLARS[taskId] = [...pillars];
});

export default function RoadToDomination() {
  const { selectedLocationId } = useLocationStore();
  const [, navigate] = useLocation();
  const { toast } = useToast();

  // Whop redirects buyers back here with ?orderId=<id> after any product
  // purchase. Confirm the payment with the server, show a toast, and refresh
  // the progress query so the graphic visibly reflects the new purchase.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const orderId = params.get("orderId");
    const sessionId = params.get("session_id");
    if (!orderId && !sessionId) return;
    const qs = orderId
      ? `orderId=${encodeURIComponent(orderId)}`
      : `session_id=${encodeURIComponent(sessionId!)}`;
    apiRequest("GET", `/api/citation-orders/verify-session?${qs}`)
      .then((r) => r.json())
      .then((order: any) => {
        if (order?.status === "paid") {
          toast({
            title: "Payment confirmed!",
            description: "Your purchase is live — watch your Road to Domination update.",
          });
          queryClient.invalidateQueries({ queryKey: ["/api/locations"] });
          queryClient.invalidateQueries({ queryKey: ["/api/citation-orders"] });
        }
        window.history.replaceState({}, "", "/road-to-domination");
      })
      .catch(() => {
        window.history.replaceState({}, "", "/road-to-domination");
      });
  }, []);

  const { data: locations } = useQuery<any[]>({
    queryKey: ["/api/locations"],
  });

  const locationId = selectedLocationId || locations?.[0]?.id || "";

  const [selectedLevel, setSelectedLevel] = useState(1);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  const { data: progress } = useQuery<{
    completedTasks: string[];
    autoCompletedTasks?: string[];
    manualCompletedTasks?: string[];
    gbpAnalysis?: {
      photos: { count: number; lastAddedAt: string | null };
      reviews: { count: number; lastAddedAt: string | null };
      products: { count: number; lastAddedAt: string | null };
      postsEngine: { enabled: boolean; totalPosts: number };
      publishedPosts: { count: number; lastAt: string | null };
      scheduledPosts: { count: number; nextAt: string | null };
      socials: { connectedCount: number };
    };
  }>({
    queryKey: ["/api/locations", locationId, "rtd-progress"],
    queryFn: async () => {
      const res = await fetch(`/api/locations/${locationId}/rtd-progress`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch progress");
      return res.json();
    },
    enabled: !!locationId,
    refetchInterval: 15000,
    staleTime: 0,
    refetchOnMount: "always",
  });

  const completedTasks = new Set<string>(progress?.completedTasks ?? []);
  const autoCompletedTasks = new Set<string>(progress?.autoCompletedTasks ?? []);

  const toggleManualMutation = useMutation({
    mutationFn: async ({ taskId, completed }: { taskId: string; completed: boolean }) => {
      return apiRequest("POST", `/api/locations/${locationId}/rtd-manual-task`, { taskId, completed });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/locations", locationId, "rtd-progress"] });
    },
  });

  const resetManualMutation = useMutation({
    mutationFn: async () => apiRequest("POST", `/api/locations/${locationId}/rtd-manual-reset`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/locations", locationId, "rtd-progress"] });
    },
  });

  const currentLocation = locations?.find((l: any) => l.id === locationId);

  const activeNodes = new Set<string>();
  completedTasks.forEach((t) => {
    (TASK_TO_NODES[t] || []).forEach((n) => activeNodes.add(n));
  });
  Object.entries(HUB_RULES).forEach(([hub, children]) => {
    if (children.filter((c) => activeNodes.has(c)).length >= 2) activeNodes.add(hub);
  });
  if (["relevance", "prominence", "proximity"].some((h) => activeNodes.has(h))) {
    activeNodes.add("visibility");
  }

  const totalTasks = LEVELS.reduce((s, l) => s + l.tasks.length, 0);
  const allTasksComplete = completedTasks.size >= totalTasks;

  const pillarScore = (hub: string) => {
    if (allTasksComplete) return 100;
    const ch = HUB_RULES[hub];
    if (!ch) return 0;
    return Math.round((ch.filter((c) => activeNodes.has(c)).length / ch.length) * 100);
  };

  const currentLevel = allTasksComplete ? 15 : completedTasks.size;

  const isAutoDetected = (taskId: string) => autoCompletedTasks.has(taskId);

  const GBP_ANALYSIS_TASKS = new Set([
    "images", "reviews",
    "posts", "publish_post", "schedule_post", "products", "publish_product",
    "social_sync", "missing_profiles",
  ]);
  const isGbpAnalysisTask = (taskId: string) => GBP_ANALYSIS_TASKS.has(taskId);

  const toggleTask = (taskId: string) => {
    if (!locationId) return;
    if (isAutoDetected(taskId)) return;
    if (isGbpAnalysisTask(taskId)) return;
    const completed = !completedTasks.has(taskId);
    toggleManualMutation.mutate({ taskId, completed });
  };

  const formatRelative = (iso: string | null | undefined) => {
    if (!iso) return null;
    const t = new Date(iso).getTime();
    if (!Number.isFinite(t)) return null;
    const days = Math.floor((Date.now() - t) / 86400000);
    if (days <= 0) return "today";
    if (days === 1) return "yesterday";
    if (days < 30) return `${days} days ago`;
    const months = Math.floor(days / 30);
    if (months === 1) return "1 month ago";
    if (months < 12) return `${months} months ago`;
    const years = Math.floor(days / 365);
    return years === 1 ? "1 year ago" : `${years} years ago`;
  };

  const formatFuture = (iso: string | null | undefined) => {
    if (!iso) return null;
    const t = new Date(iso).getTime();
    if (!Number.isFinite(t)) return null;
    const days = Math.ceil((t - Date.now()) / 86400000);
    if (days <= 0) return "today";
    if (days === 1) return "tomorrow";
    if (days < 30) return `in ${days} days`;
    const months = Math.ceil(days / 30);
    return months === 1 ? "in 1 month" : `in ${months} months`;
  };

  const gbpBadgeFor = (taskId: string): { message: string; hasData: boolean } | null => {
    const a = progress?.gbpAnalysis;
    if (!a) return null;
    switch (taskId) {
      case "images": {
        const rel = formatRelative(a.photos.lastAddedAt);
        return a.photos.count > 0
          ? { message: `GBP scan: ${a.photos.count} photos · last added ${rel ?? "unknown"}`, hasData: true }
          : { message: "GBP scan: no photos found — sync your GBP to register activity", hasData: false };
      }
      case "reviews": {
        const rel = formatRelative(a.reviews.lastAddedAt);
        return a.reviews.count > 0
          ? { message: `GBP scan: ${a.reviews.count} reviews · last added ${rel ?? "unknown"}`, hasData: true }
          : { message: "GBP scan: no reviews found — sync your GBP to register activity", hasData: false };
      }
      case "posts": {
        if (a.postsEngine.enabled) {
          return { message: "GBP scan: posts engine active", hasData: true };
        }
        if (a.postsEngine.totalPosts > 0) {
          return { message: `GBP scan: ${a.postsEngine.totalPosts} posts found · engine off — enable autopilot to keep posting`, hasData: true };
        }
        return { message: "GBP scan: posts engine not enabled — turn on autopilot to activate", hasData: false };
      }
      case "publish_post": {
        const rel = formatRelative(a.publishedPosts.lastAt);
        return a.publishedPosts.count > 0
          ? { message: `GBP scan: ${a.publishedPosts.count} posts published · last ${rel ?? "unknown"}`, hasData: true }
          : { message: "GBP scan: no published posts found — publish your first post to GBP", hasData: false };
      }
      case "schedule_post": {
        const fut = formatFuture(a.scheduledPosts.nextAt);
        return a.scheduledPosts.count > 0
          ? { message: `GBP scan: ${a.scheduledPosts.count} posts scheduled${fut ? ` · next ${fut}` : ""}`, hasData: true }
          : { message: "GBP scan: nothing scheduled — queue a post for later", hasData: false };
      }
      case "products": {
        const rel = formatRelative(a.products.lastAddedAt);
        return a.products.count > 0
          ? { message: `GBP scan: ${a.products.count} products in forge · last added ${rel ?? "unknown"}`, hasData: true }
          : { message: "GBP scan: no products in forge yet — generate your first product", hasData: false };
      }
      case "publish_product": {
        const rel = formatRelative(a.products.lastAddedAt);
        return a.products.count > 0
          ? { message: `GBP scan: ${a.products.count} products · last added ${rel ?? "unknown"}`, hasData: true }
          : { message: "GBP scan: no products published — publish a product to your GBP", hasData: false };
      }
      case "social_sync": {
        return a.socials.connectedCount >= 2
          ? { message: `GBP scan: ${a.socials.connectedCount} social profiles connected`, hasData: true }
          : { message: `GBP scan: only ${a.socials.connectedCount} social profile${a.socials.connectedCount === 1 ? "" : "s"} connected — sync at least 2`, hasData: false };
      }
      case "missing_profiles": {
        return a.socials.connectedCount >= 5
          ? { message: `GBP scan: ${a.socials.connectedCount} social profiles connected`, hasData: true }
          : { message: `GBP scan: only ${a.socials.connectedCount} of 5+ platforms connected — claim the missing ones`, hasData: false };
      }
    }
    return null;
  };

  const allTasks = LEVELS.flatMap((l) => l.tasks);
  const canDoTask = (taskId: string) => {
    const idx = allTasks.findIndex((t) => t.id === taskId);
    if (idx === 0) return true;
    return completedTasks.has(allTasks[idx - 1].id);
  };
  const isLevelUnlocked = (levelId: number) => {
    const level = LEVELS.find((l) => l.id === levelId);
    if (!level) return false;
    return canDoTask(level.tasks[0].id);
  };

  const prevCompletedSizeRef = useRef(completedTasks.size);
  useEffect(() => {
    const prevSize = prevCompletedSizeRef.current;
    prevCompletedSizeRef.current = completedTasks.size;
    if (completedTasks.size <= prevSize) return;
    const level = LEVELS.find((l) => l.id === selectedLevel);
    if (level && level.tasks.every((t) => completedTasks.has(t.id))) {
      const nextLevel = LEVELS.find((l) => l.id === selectedLevel + 1);
      if (nextLevel && isLevelUnlocked(nextLevel.id)) {
        setSelectedLevel(nextLevel.id);
      }
    }
  }, [completedTasks, selectedLevel]);

  // ── Phase-completion celebration ────────────────────────────────────────
  // When the user lands on this page (or finishes a task while on it) and
  // an entire phase has just been completed for the first time, fire a
  // dopamine-spiking overlay. Tracked per-location in localStorage so each
  // phase only celebrates once. We bind the celebration to the location
  // that triggered it so a mid-celebration location switch doesn't write
  // the wrong location's "celebrated" flag.
  const [celebration, setCelebration] = useState<
    { phase: RtdPhase; locationId: string } | null
  >(null);
  useEffect(() => {
    if (!locationId) return;
    if (celebration) return;
    const next = getNewlyCompletedPhase(locationId, completedTasks);
    if (next) setCelebration({ phase: next, locationId });
  }, [locationId, completedTasks, celebration]);
  const dismissCelebration = () => {
    if (celebration) {
      markPhaseCelebrated(celebration.locationId, celebration.phase.id);
    }
    setCelebration(null);
  };

  const drawNetwork = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    const W = rect.width;
    const H = rect.height;
    ctx.clearRect(0, 0, W, H);

    const getPos = (node: NetworkNode) => ({ x: node.x * W, y: node.y * H });
    const time = Date.now() / 1000;

    ctx.fillStyle = "#ffffff05";
    for (let x = 15; x < W; x += 28) {
      for (let y = 15; y < H; y += 28) {
        ctx.beginPath();
        ctx.arc(x, y, 0.5, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    const drawRegion = (cx: number, cy: number, radius: number, color: string) => {
      const grad = ctx.createRadialGradient(cx * W, cy * H, 0, cx * W, cy * H, radius * Math.min(W, H));
      grad.addColorStop(0, `${color}0c`);
      grad.addColorStop(1, `${color}00`);
      ctx.beginPath();
      ctx.arc(cx * W, cy * H, radius * Math.min(W, H), 0, Math.PI * 2);
      ctx.fillStyle = grad;
      ctx.fill();
    };
    if (activeNodes.has("relevance")) drawRegion(0.22, 0.27, 0.32, PILLAR_COLORS.relevance);
    if (activeNodes.has("prominence")) drawRegion(0.78, 0.27, 0.32, PILLAR_COLORS.prominence);
    if (activeNodes.has("proximity")) drawRegion(0.5, 0.78, 0.28, PILLAR_COLORS.proximity);
    if (activeNodes.has("visibility")) drawRegion(0.5, 0.5, 0.18, PILLAR_COLORS.core);

    NETWORK_EDGES.forEach(([fromId, toId]) => {
      const from = NETWORK_NODES.find((n) => n.id === fromId);
      const to = NETWORK_NODES.find((n) => n.id === toId);
      if (!from || !to) return;
      const p1 = getPos(from);
      const p2 = getPos(to);
      const bothActive = activeNodes.has(fromId) && activeNodes.has(toId);

      ctx.beginPath();
      ctx.moveTo(p1.x, p1.y);
      ctx.lineTo(p2.x, p2.y);

      if (bothActive) {
        const color = PILLAR_COLORS[to.group] || PILLAR_COLORS[from.group];
        const isMainArtery = ["relevance", "prominence", "proximity"].includes(fromId) && toId === "visibility";
        ctx.strokeStyle = isMainArtery ? `${color}60` : `${color}35`;
        ctx.lineWidth = isMainArtery ? 3 : 1.8;
        ctx.stroke();

        const speed = isMainArtery ? 0.5 : 0.3;
        const offset = (fromId.charCodeAt(0) + toId.charCodeAt(0)) * 0.13;
        const t = ((time * speed + offset) % 1);
        const px = p1.x + (p2.x - p1.x) * t;
        const py = p1.y + (p2.y - p1.y) * t;
        ctx.beginPath();
        ctx.arc(px, py, isMainArtery ? 3 : 1.5, 0, Math.PI * 2);
        ctx.fillStyle = `${color}90`;
        ctx.fill();

        if (isMainArtery) {
          const t2 = ((time * speed + offset + 0.5) % 1);
          const px2 = p1.x + (p2.x - p1.x) * t2;
          const py2 = p1.y + (p2.y - p1.y) * t2;
          ctx.beginPath();
          ctx.arc(px2, py2, 2, 0, Math.PI * 2);
          ctx.fillStyle = `${color}60`;
          ctx.fill();
        }
      } else {
        ctx.strokeStyle = "#ffffff06";
        ctx.lineWidth = 0.5;
        ctx.setLineDash([3, 4]);
        ctx.stroke();
        ctx.setLineDash([]);
      }
    });

    NETWORK_NODES.forEach((node) => {
      const pos = getPos(node);
      const isActive = activeNodes.has(node.id);
      const isPillar = ["relevance", "prominence", "proximity"].includes(node.id);
      const isCore = node.id === "visibility";
      const color = PILLAR_COLORS[node.group];
      const scale = Math.min(W, H) / 700;
      const baseR = node.size * scale * 0.5;
      const breathe = (isPillar || isCore) ? Math.sin(time * 1.2 + node.x * 5) * 1.5 : 0;
      const r = baseR + breathe;

      if (isActive && (isPillar || isCore)) {
        const grad = ctx.createRadialGradient(pos.x, pos.y, r * 0.2, pos.x, pos.y, r * 3.5);
        grad.addColorStop(0, `${color}22`);
        grad.addColorStop(1, `${color}00`);
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, r * 3.5, 0, Math.PI * 2);
        ctx.fillStyle = grad;
        ctx.fill();
      }

      if (isActive && !isPillar && !isCore) {
        const pulseR = r + Math.sin(time * 2 + node.y * 3) * 2 + 4;
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, pulseR, 0, Math.PI * 2);
        ctx.strokeStyle = `${color}12`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      ctx.beginPath();
      ctx.arc(pos.x, pos.y, r, 0, Math.PI * 2);
      if (isActive) {
        const grad = ctx.createRadialGradient(pos.x - r * 0.2, pos.y - r * 0.2, 0, pos.x, pos.y, r);
        grad.addColorStop(0, `${color}38`);
        grad.addColorStop(1, `${color}10`);
        ctx.fillStyle = grad;
      } else {
        ctx.fillStyle = "#0a0a12";
      }
      ctx.fill();
      ctx.strokeStyle = isActive ? `${color}aa` : "#ffffff0c";
      ctx.lineWidth = (isPillar || isCore) ? 2.5 : isActive ? 1.5 : 0.5;
      ctx.stroke();

      if (isPillar && isActive) {
        const score = pillarScore(node.id);
        ctx.fillStyle = `${color}dd`;
        ctx.font = `bold ${r * 0.45}px 'Space Grotesk', sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(`${score}%`, pos.x, pos.y);
      }

      if (isCore && isActive) {
        const avg = Math.round((pillarScore("relevance") + pillarScore("prominence") + pillarScore("proximity")) / 3);
        ctx.fillStyle = "#ff8c00dd";
        ctx.font = `bold ${r * 0.48}px 'Space Grotesk', sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(`${avg}`, pos.x, pos.y - r * 0.06);
        ctx.fillStyle = "#ff8c0068";
        ctx.font = `500 ${r * 0.18}px 'JetBrains Mono', monospace`;
        ctx.fillText("SCORE", pos.x, pos.y + r * 0.32);
      }

      if (!isPillar && !isCore) {
        ctx.fillStyle = isActive ? "#ffffffbb" : "#ffffff16";
        const fs = Math.max(7, r * 0.46);
        ctx.font = `400 ${fs}px 'JetBrains Mono', monospace`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        node.label.split("\n").forEach((line, i, arr) => {
          ctx.fillText(line, pos.x, pos.y + (i - (arr.length - 1) / 2) * (fs + 1));
        });
      }
      if (isPillar) {
        ctx.fillStyle = isActive ? `${color}cc` : "#ffffff16";
        const fs = Math.max(10, r * 0.34);
        ctx.font = `600 ${fs}px 'Space Grotesk', sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "bottom";
        ctx.fillText(node.label, pos.x, pos.y - r - 7);
      }
      if (isCore) {
        ctx.fillStyle = isActive ? "#ff8c00bb" : "#ffffff16";
        const fs = Math.max(10, r * 0.28);
        ctx.font = `700 ${fs}px 'Space Grotesk', sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "bottom";
        ctx.fillText("Visibility", pos.x, pos.y - r - 9);
      }
    });

    animRef.current = requestAnimationFrame(drawNetwork);
  }, [activeNodes, completedTasks]);

  useEffect(() => {
    drawNetwork();
    return () => cancelAnimationFrame(animRef.current);
  }, [drawNetwork]);

  const levelColors = ["#6b7280", "#e8732a", "#f59e0b", "#4ecdc4", "#a78bfa"];

  if (!locationId) {
    return (
      <div className="flex items-center justify-center h-full" data-testid="container-no-location">
        <div className="text-center text-muted-foreground">
          <p className="text-lg font-semibold mb-2">Select a location to begin</p>
          <p className="text-sm">Choose a location from the sidebar to start your Road to Domination</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ height: "100%", background: "#08080e", color: "#e0e0e0", fontFamily: "'JetBrains Mono', 'SF Mono', monospace", display: "flex", flexDirection: "column", overflow: "hidden" }} data-testid="container-road-to-domination">
      <link href="https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@300;400;500;600;700&family=Space+Grotesk:wght@400;500;600;700&display=swap" rel="stylesheet" />

      <div style={{ padding: "12px 20px", borderBottom: "1px solid #ffffff08", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, flexShrink: 0 }} data-testid="header-bar">
        <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
          <span style={{ fontFamily: "'Space Grotesk'", fontSize: 18, fontWeight: 700, color: "#e8732a", letterSpacing: "-0.5px" }}>Road to Domination</span>
          <span style={{ fontSize: 9, color: "#ffffff18" }}>Relevance · Prominence · Proximity</span>
          {currentLocation && (
            <span style={{ fontSize: 10, color: "#ffffff30", marginLeft: 4 }}>— {currentLocation.name}</span>
          )}
        </div>

        <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
          {([
            { key: "relevance", label: "RELEVANCE", emoji: "\uD83C\uDFAF" },
            { key: "prominence", label: "PROMINENCE", emoji: "\u2B50" },
            { key: "proximity", label: "PROXIMITY", emoji: "\uD83D\uDCCD" },
          ] as const).map(({ key, label, emoji }) => {
            const score = pillarScore(key);
            return (
              <div key={key} style={{ display: "flex", alignItems: "center", gap: 5 }} data-testid={`indicator-${key}`}>
                <div style={{ position: "relative", width: 30, height: 30 }}>
                  <svg width="30" height="30" viewBox="0 0 30 30">
                    <circle cx="15" cy="15" r="12" fill="none" stroke="#ffffff0a" strokeWidth="2.5" />
                    <circle cx="15" cy="15" r="12" fill="none"
                      stroke={score > 0 ? PILLAR_COLORS[key] : "#ffffff0a"}
                      strokeWidth="2.5"
                      strokeDasharray={`${score * 0.754} 75.4`}
                      strokeLinecap="round"
                      transform="rotate(-90 15 15)"
                      style={{ transition: "stroke-dasharray 0.6s ease" }}
                    />
                  </svg>
                  <span style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10 }}>{emoji}</span>
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, fontFamily: "'Space Grotesk'", color: score > 0 ? PILLAR_COLORS[key] : "#ffffff20", lineHeight: 1 }}>{score}<span style={{ fontSize: 9, fontWeight: 400 }}>%</span></div>
                  <div style={{ fontSize: 6, color: "#ffffff20", letterSpacing: "0.1em" }}>{label}</div>
                </div>
              </div>
            );
          })}

          <div style={{ width: 1, height: 26, background: "#ffffff08" }} />

          <div
            onClick={() => {
              if (locationId && confirm("Reset all Road to Domination progress for this location?")) {
                resetManualMutation.mutate();
              }
            }}
            style={{ fontSize: 8, color: "#ffffff20", cursor: "pointer", padding: "4px 8px", borderRadius: 4, border: "1px solid #ffffff10" }}
            data-testid="button-reset-progress"
          >RESET</div>

          <div style={{ width: 1, height: 26, background: "#ffffff08" }} />

          <div style={{ textAlign: "center" }} data-testid="indicator-level">
            <div style={{ fontSize: 14, fontWeight: 700, fontFamily: "'Space Grotesk'", color: "#ff8c00", lineHeight: 1 }}>
              LVL {currentLevel}<span style={{ fontSize: 9, fontWeight: 400, color: "#ffffff30", marginLeft: 4 }}>/ {totalTasks}</span>
            </div>
            <div style={{ height: 2, background: "#ffffff08", borderRadius: 1, marginTop: 4, width: 60, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${Math.round((currentLevel / totalTasks) * 100)}%`, background: "linear-gradient(90deg, #e8732a, #ff8c00)", borderRadius: 1, transition: "width 0.5s" }} />
            </div>
          </div>
        </div>
      </div>

      {allTasksComplete ? (
        <div style={{ flex: 1, overflow: "hidden" }} data-testid="panel-level-fifteen">
          <React.Suspense fallback={null}>
            <LevelFifteenPage embedded />
          </React.Suspense>
        </div>
      ) : (
      <div style={{ display: "grid", gridTemplateColumns: "420px 1fr", flex: 1, overflow: "hidden" }}>
        <div style={{ borderRight: "1px solid #ffffff08", overflowY: "auto", display: "flex", flexDirection: "column" }} data-testid="panel-tasks">
          {(() => {
            // The next level the user should jump into: first unlocked, not-yet-done
            // level. We pulse its tab number to draw the eye after they finish a level.
            const nextLevelId = LEVELS.find(
              (l) => isLevelUnlocked(l.id) && !l.tasks.every((t) => completedTasks.has(t.id))
            )?.id;
            return (
              <div style={{ display: "flex", borderBottom: "1px solid #ffffff08", flexShrink: 0 }}>
                {LEVELS.map((level) => {
                  const unlocked = isLevelUnlocked(level.id);
                  const done = level.tasks.every((t) => completedTasks.has(t.id));
                  const active = selectedLevel === level.id;
                  const color = levelColors[level.id];
                  // Pulse only when this is the next-to-do level AND the user isn't
                  // already viewing it (no need to nudge them somewhere they already are).
                  const shouldPulse = !done && unlocked && level.id === nextLevelId && !active;
                  return (
                    <div key={level.id} onClick={() => unlocked && setSelectedLevel(level.id)}
                      style={{
                        flex: 1, padding: "10px 2px 8px", textAlign: "center", cursor: unlocked ? "pointer" : "default",
                        borderBottom: active ? `2px solid ${color}` : "2px solid transparent",
                        opacity: unlocked ? 1 : 0.22, transition: "all 0.15s",
                      }}
                      data-testid={`tab-level-${level.id}`}
                    >
                      <div
                        className={shouldPulse ? "tab-number-pulse" : undefined}
                        style={
                          shouldPulse
                            ? { fontSize: 14, fontWeight: 700 }
                            : { fontSize: 14, fontWeight: 700, color: done ? color : active ? "#fff" : "#ffffff40" }
                        }
                      >
                        {done ? "\u2713" : level.id}
                      </div>
                      <div style={{ fontSize: 11, fontWeight: 500, color: active ? "#ffffffb0" : "#ffffff70", marginTop: 2, letterSpacing: "0.04em" }}>
                        {unlocked ? level.name.toUpperCase() : "\uD83D\uDD12"}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {(() => {
            const level = LEVELS.find((l) => l.id === selectedLevel);
            if (!level) return null;
            const doneCount = level.tasks.filter((t) => completedTasks.has(t.id)).length;
            const color = levelColors[level.id];

            return (
              <div style={{ padding: "16px 14px", flex: 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 2 }}>
                  <span style={{ fontFamily: "'Space Grotesk'", fontSize: 22, fontWeight: 600, color }}>{level.name}</span>
                  <span style={{ fontSize: 15, fontWeight: 500, color: "#ffffff85" }}>{doneCount}/{level.tasks.length}</span>
                </div>
                <div style={{ fontSize: 15, color: "#ffffff95", marginBottom: 12, lineHeight: 1.4 }}>{level.subtitle}</div>

                <div style={{ height: 3, background: "#ffffff08", borderRadius: 2, marginBottom: 14, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${(doneCount / level.tasks.length) * 100}%`, background: color, borderRadius: 2, transition: "width 0.4s" }} />
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                  {(() => {
                    const firstIncompleteIdx = level.tasks.findIndex((t) => !completedTasks.has(t.id) && canDoTask(t.id));
                    return level.tasks.map((task, taskIdx) => {
                    const done = completedTasks.has(task.id);
                    const canDo = canDoTask(task.id);
                    const pillars = TASK_PILLARS[task.id] || [];
                    const isNextAction = !done && canDo && taskIdx === firstIncompleteIdx;

                    return (
                      <div key={task.id} style={{
                        display: "flex", alignItems: "flex-start", gap: 10,
                        padding: "9px 10px", borderRadius: 6, opacity: canDo ? 1 : 0.25,
                        transition: "background 0.12s", cursor: canDo ? "pointer" : "default",
                        background: isNextAction ? `${color}08` : "transparent",
                        border: isNextAction ? `1px solid ${color}20` : "1px solid transparent",
                      }}
                        onMouseEnter={(e) => { if (canDo) (e.currentTarget as HTMLElement).style.background = isNextAction ? `${color}12` : "#ffffff06"; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = isNextAction ? `${color}08` : "transparent"; }}
                        data-testid={`task-${task.id}`}
                      >
                        <div onClick={(e) => { e.stopPropagation(); if (canDo) toggleTask(task.id); }} style={{
                          width: 18, height: 18, minWidth: 18, borderRadius: 4,
                          border: `1.5px solid ${done ? color : isNextAction ? `${color}60` : "#ffffff15"}`,
                          background: done ? `${color}20` : isNextAction ? `${color}10` : "transparent",
                          display: "flex", alignItems: "center", justifyContent: "center", marginTop: 1,
                          cursor: canDo ? "pointer" : "default", transition: "transform 0.12s",
                        }}
                          data-testid={`checkbox-${task.id}`}
                        >
                          {done && (
                            <svg width="10" height="10" viewBox="0 0 12 12">
                              <path d="M2 6l3 3 5-5" stroke={color} strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          )}
                        </div>

                        <div style={{ flex: 1, minWidth: 0 }} onClick={() => { if (canDo && task.route) navigate(task.route); }}>
                          <div style={{ fontSize: 16, fontWeight: 500, color: done ? color : "#ffffffe5", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                            {task.label}
                            {done && isAutoDetected(task.id) && (
                              <span style={{ fontSize: 11, padding: "1px 6px", borderRadius: 3, background: `${color}20`, color, border: `1px solid ${color}30`, fontWeight: 600, letterSpacing: "0.05em" }}>AUTO</span>
                            )}
                            {isNextAction && (
                              <span style={{ fontSize: 11, padding: "2px 7px", borderRadius: 3, background: `${color}30`, color, border: `1px solid ${color}45`, fontWeight: 700, letterSpacing: "0.06em", animation: "pulse 2s ease-in-out infinite" }}>DO THIS NEXT</span>
                            )}
                          </div>
                          <div style={{ fontSize: 14, color: isNextAction ? "#ffffffa5" : "#ffffff80", marginTop: 3, lineHeight: 1.45 }}>{task.desc}</div>

                          {isGbpAnalysisTask(task.id) && (() => {
                            const badge = gbpBadgeFor(task.id);
                            if (!badge) return null;
                            return (
                              <div
                                style={{
                                  display: "inline-flex", alignItems: "center", gap: 6,
                                  marginTop: 6, padding: "3px 7px", borderRadius: 4,
                                  background: badge.hasData ? `${color}10` : "#ffffff06",
                                  border: `1px solid ${badge.hasData ? `${color}22` : "#ffffff10"}`,
                                  fontSize: 14, color: badge.hasData ? color : "#ffffff95", fontWeight: 500,
                                }}
                                data-testid={`gbp-analysis-${task.id}`}
                              >
                                {badge.message}
                              </div>
                            );
                          })()}

                          {pillars.length > 0 && (
                            <div style={{ display: "flex", gap: 3, marginTop: 5, flexWrap: "wrap" }}>
                              {pillars.map((p) => (
                                <span key={p} style={{
                                  fontSize: 11, padding: "2px 7px", borderRadius: 3,
                                  background: done ? `${PILLAR_COLORS[p]}20` : "#ffffff10",
                                  color: done ? PILLAR_COLORS[p] : "#ffffff70",
                                  border: `1px solid ${done ? `${PILLAR_COLORS[p]}30` : "#ffffff15"}`,
                                  textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600,
                                }}>{p === "relevance" ? "\uD83C\uDFAF Relevance" : p === "prominence" ? "\u2B50 Prominence" : "\uD83D\uDCCD Proximity"}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  });
                  })()}
                </div>

                {(() => {
                  const nextLevel = LEVELS.find((l) => l.id === level.id + 1);
                  const allDone = doneCount === level.tasks.length;
                  if (!allDone || !nextLevel) return null;
                  return (
                    <div style={{
                      marginTop: 14, padding: "8px 10px", borderRadius: 6,
                      border: `1px solid ${levelColors[nextLevel.id]}18`,
                      background: `${levelColors[nextLevel.id]}06`,
                    }}>
                      <div style={{ fontSize: 14, color: levelColors[nextLevel.id], fontWeight: 600, letterSpacing: "0.03em" }}>
                        {"\u2705"} LEVEL {level.id} COMPLETE — {nextLevel.name.toUpperCase()} UNLOCKED
                      </div>
                    </div>
                  );
                })()}
              </div>
            );
          })()}
        </div>

        <div style={{ position: "relative", background: "#06060a" }} data-testid="panel-network">
          <div style={{ position: "absolute", top: 12, left: 16, zIndex: 2 }}>
            <div style={{ fontSize: 9, color: "#ffffff1a", fontWeight: 600, letterSpacing: "0.1em" }}>VISIBILITY NETWORK</div>
            <div style={{ fontSize: 8, color: "#ffffff10", marginTop: 2 }}>Complete tasks to light up nodes</div>
          </div>
          <div style={{ position: "absolute", top: 12, right: 16, zIndex: 2, textAlign: "right" }}>
            <div style={{ fontSize: 20, fontWeight: 700, fontFamily: "'Space Grotesk'", color: "#ff8c00" }}>{activeNodes.size}</div>
            <div style={{ fontSize: 8, color: "#ffffff15" }}>/ {NETWORK_NODES.length} nodes</div>
          </div>
          <div style={{ position: "absolute", bottom: 14, left: 20, zIndex: 2, display: "flex", gap: 20 }}>
            {[
              { label: "Relevance", color: PILLAR_COLORS.relevance },
              { label: "Prominence", color: PILLAR_COLORS.prominence },
              { label: "Proximity", color: PILLAR_COLORS.proximity },
            ].map((g) => (
              <div key={g.label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{ width: 10, height: 10, borderRadius: "50%", background: g.color, opacity: 0.8 }} />
                <span style={{ fontSize: 13, color: "#ffffff60", fontWeight: 500 }}>{g.label}</span>
              </div>
            ))}
          </div>
          <canvas ref={canvasRef} style={{ width: "100%", height: "100%", display: "block" }} data-testid="canvas-network" />
        </div>
      </div>
      )}
      <PhaseCelebration
        phase={celebration?.phase ?? null}
        onDismiss={dismissCelebration}
        totalPhases={RTD_PHASES.length}
      />
    </div>
  );
}
